import React, { useState, useEffect } from 'react';
import { useStore } from '../../store';
import { Folder, X, Edit2, Trash2, Plus } from 'lucide-react';

const ICONS = ['📁', '📂', '📋', '🚀', '💡', '🔥', '🎯', '🛠', '🧩', '📊', '🌟'];
const COLORS = ['#4f63ff', '#f59e0b', '#10b981', '#ef4444', '#a78bfa', '#f97316', '#06b6d4', '#ec4899'];

export default function EditGroupModal({ group, onClose }) {
  const [form, setForm] = useState({ name: group.name, icon: group.icon, color: group.color });
  const [loading, setLoading] = useState(false);
  const { updateProjectGroup, deleteProjectGroup, projectGroups, projects, fetchProjectGroups, fetchProjects } = useStore();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddProject, setShowAddProject] = useState(false);

  const getGroupId = (p) => (typeof p.group === 'string' ? p.group : p.group?._id || null);

  useEffect(() => {
    setForm({ name: group.name, icon: group.icon, color: group.color });
  }, [group]);

  const submit = async e => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      await updateProjectGroup(group._id, form);
      onClose();
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProjectGroup(group._id);
      onClose();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAddProject = async (projectId) => {
    try {
      await updateProjectGroup(group._id, {
        $addToSet: { projects: projectId }
      });
      setShowAddProject(false);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRemoveProject = async (projectId) => {
    try {
      await updateProjectGroup(group._id, {
        $pull: { projects: projectId }
      });
    } catch (err) {
      alert(err.message);
    }
  };

  const groupProjects = projects.filter(p => getGroupId(p) === group._id);
  const otherProjects = projects.filter(p => !getGroupId(p) || getGroupId(p) !== group._id);

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal p-6 max-w-md max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold">Редактировать группу</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label">Иконка</label>
            <div className="flex gap-1.5 flex-wrap">
              {ICONS.map(ic => (
                <button key={ic} type="button" onClick={() => setForm(p => ({ ...p, icon: ic }))}
                  className={`w-9 h-9 rounded-lg text-lg flex items-center justify-center transition-all ${form.icon === ic ? 'ring-2 ring-brand-500 bg-brand-50 dark:bg-brand-900' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
                  {ic}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label">Цвет</label>
            <div className="flex gap-2">
              {COLORS.map(c => (
                <button key={c} type="button" onClick={() => setForm(p => ({ ...p, color: c }))}
                  className={`w-7 h-7 rounded-full transition-transform ${form.color === c ? 'scale-125 ring-2 ring-offset-1 ring-gray-400' : 'hover:scale-110'}`}
                  style={{ background: c }} />
              ))}
            </div>
          </div>

          <div>
            <label className="label">Название группы *</label>
            <input className="input" placeholder="Название группы" value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label">Проекты в группе ({groupProjects.length})</label>
              {otherProjects.length > 0 && (
                <button type="button" onClick={() => setShowAddProject(true)} className="text-xs text-brand-600 hover:text-brand-700 flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Добавить
                </button>
              )}
            </div>
            {groupProjects.length === 0 ? (
              <div className="text-sm text-gray-400 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                Нет проектов в группе
              </div>
            ) : (
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {groupProjects.map(p => (
                  <div key={p._id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm">
                    <span>{p.icon} {p.name}</span>
                    <button type="button" onClick={() => handleRemoveProject(p._id)}
                      className="p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded text-red-500">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={() => setShowDeleteConfirm(true)} className="btn-secondary justify-center text-red-600 hover:text-red-700">
              <Trash2 className="w-4 h-4" />
            </button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Отмена</button>
            <button type="submit" className="btn-primary flex-1 justify-center" disabled={loading}>
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>

        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
              <h3 className="font-semibold mb-2">Удалить группу?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Проекты не будут удалены, они просто уберутся из группы.
              </p>
              <div className="flex gap-2">
                <button onClick={() => setShowDeleteConfirm(false)} className="btn-secondary flex-1 justify-center">Отмена</button>
                <button onClick={handleDelete} className="btn-danger flex-1 justify-center">Удалить</button>
              </div>
            </div>
          </div>
        )}

        {showAddProject && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Добавить проект</h3>
                <button onClick={() => setShowAddProject(false)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {otherProjects.map(p => (
                  <button key={p._id} onClick={() => handleAddProject(p._id)}
                    className="w-full text-left p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg flex items-center gap-2">
                    <span>{p.icon}</span>
                    <span className="text-sm">{p.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
